import React, { useState } from 'react';
import { 
  Sparkles, Target, TrendingUp, BarChart3, Zap, Shield,
  Users, Globe, Award, CheckCircle, ArrowRight, Brain,
  Layers, GitBranch, Eye, Network, Clock, DollarSign,
  Activity, Lightbulb, Search, Image as ImageIcon, MessageSquare
} from 'lucide-react';

function About() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: Sparkles },
    { id: 'capabilities', label: 'Capabilities', icon: Target },
    { id: 'agents', label: 'AI Agents', icon: Brain },
    { id: 'technical', label: 'Technical', icon: Layers },
    { id: 'value', label: 'Value Proposition', icon: Award }
  ];

  const capabilities = [
    {
      icon: BarChart3,
      color: 'var(--primary-500)',
      title: 'Campaign Management',
      description: 'Comprehensive campaign analytics with effectiveness scoring, efficiency percentiles, and data quality tracking.',
      features: [
        'Multi-channel campaign tracking (Search, Social, Display, Video, Email)',
        '30-day time-series metrics with 4 chart visualizations',
        'Effectiveness scoring (75-95%) with color-coded indicators',
        'Data quality assessment (3-tier scoring, A-F grading)',
        'Bulk campaign operations and advanced filtering',
        'Campaign detail modals with performance benchmarking'
      ]
    },
    {
      icon: Lightbulb,
      color: 'var(--accent-500)',
      title: 'AI-Powered Recommendations',
      description: 'Multi-agent budget optimization with full explainability and confidence scoring.',
      features: [
        '6 AI agents orchestrated via LangGraph',
        '95%+ confidence scores on all recommendations',
        'Chain-of-thought reasoning (5+ visible steps)',
        'Monte Carlo simulations (5,000 iterations)',
        '95% confidence intervals on projections',
        'Recommendation negotiation ("What if I only have $X?")',
        'RLHF feedback loop for continuous improvement'
      ]
    },
    {
      icon: Zap,
      color: 'var(--warning-500)',
      title: 'Scenario Modeling',
      description: 'What-if analysis with statistical rigor and portfolio optimization.',
      features: [
        '6 pre-built scenario templates (Aggressive, Balanced, Conservative, etc.)',
        'Real-time Monte Carlo simulations (5,000+ iterations)',
        'Efficient frontier visualization (finance-grade)',
        'Sensitivity analysis across all channels',
        'Risk/return tradeoff analysis',
        'Save/load/compare scenarios',
        'Expected returns with 95% confidence intervals'
      ]
    },
    {
      icon: Search,
      color: 'var(--info-500)',
      title: 'Competitive Intelligence',
      description: 'Real-time competitor monitoring with AI-powered market insights.',
      features: [
        'Live web search via Tavily AI (real-time results)',
        'Competitor tracking dashboard with activity scoring',
        'Market positioning map (Quality vs Price scatter plot)',
        'Market share visualization (pie charts + bars)',
        '6-month activity trend analysis',
        'AI-generated competitive insights',
        'Automated spend estimation from web mentions'
      ]
    },
    {
      icon: ImageIcon,
      color: 'var(--success-500)',
      title: 'Creative Intelligence',
      description: 'AI-powered creative analysis and brand consistency tracking.',
      features: [
        'Visual composition analysis (brightness, contrast, color)',
        'Brand consistency scoring (0-100%)',
        'Emotional tone detection via AI',
        'A/B test hypothesis generation',
        'Performance correlation tracking',
        'Creative trend analysis over quarters',
        'Brand guideline compliance monitoring'
      ]
    },
    {
      icon: MessageSquare,
      color: 'var(--accent-500)',
      title: 'Intelligent Chat',
      description: 'Conversational AI that understands marketing data and provides actionable insights.',
      features: [
        'Intent classification (6 types: campaign query, analysis, what-if, comparison, etc.)',
        'Direct MongoDB queries for real-time data',
        'Calls other AI agents on demand (Effectiveness, Scenario)',
        'Returns structured data cards (campaigns, analysis, simulations)',
        '6 dynamically generated suggested questions',
        'Conversation memory with full context',
        'Multi-modal responses (text + data visualizations)'
      ]
    },
    {
      icon: Network,
      color: 'var(--secondary-500)',
      title: 'Integration Layer',
      description: '7-layer architecture for secure data ingestion and normalization.',
      features: [
        '4 platform connectors (Google Ads, Meta Ads, Salesforce, HubSpot)',
        'LLM-powered normalization (7 objectives, 8 canonical channels)',
        '3-tier data quality scoring with anomaly detection',
        'Agent tool interface (5 permission-checked tools)',
        'Complete audit logging (3 log types)',
        'Slack/Teams workflow integration',
        'Data lineage tracking (4-step chain)'
      ]
    },
    {
      icon: Clock,
      color: 'var(--warning-500)',
      title: 'Advanced Analytics',
      description: 'Unique features for deep marketing intelligence.',
      features: [
        'Time Machine: Historical "what if we had AI" simulations',
        'Knowledge Graph: Relationship discovery, unexplored combinations',
        'RLHF Dashboard: Alignment tracking, acceptance rates',
        'Observability: Token usage, cost tracking ($2.35/campaign avg)',
        'Anomaly detection: Z-score + EWMA algorithms',
        'Predictive insights: Trend forecasting'
      ]
    }
  ];

  const agents = [
    {
      name: 'Effectiveness Agent',
      icon: TrendingUp,
      color: 'var(--success-500)',
      purpose: 'Campaign performance analysis and budget optimization',
      capabilities: [
        'Campaign effectiveness scoring (0-100%)',
        'Efficiency percentile calculations vs peers',
        'Monte Carlo budget allocation (5,000+ iterations)',
        'ROI forecasting with confidence intervals',
        'Peer benchmarking and comparative analysis'
      ],
      output: 'Recommended budget allocation with 95% confidence intervals'
    },
    {
      name: 'Data Connector Agent',
      icon: Globe,
      color: 'var(--primary-500)',
      purpose: 'Data ingestion, normalization, and quality scoring',
      capabilities: [
        'CSV parsing and schema mapping',
        'Multi-platform data normalization',
        'Data quality scoring (3-tier system)',
        'Anomaly detection (impossible values, outliers)',
        'Automated field mapping and validation'
      ],
      output: 'Normalized campaign data with quality scores (A-F grades)'
    },
    {
      name: 'Creative Agent',
      icon: ImageIcon,
      color: 'var(--warning-500)',
      purpose: 'Visual analysis and brand consistency',
      capabilities: [
        'Visual composition metrics (brightness, contrast, colors)',
        'Brand consistency scoring (78-92% typical)',
        'Emotional tone detection (professional, energetic, bold)',
        'A/B test hypothesis generation (3-5 per creative)',
        'Performance prediction based on visual elements'
      ],
      output: 'Brand consistency score, visual metrics, A/B test hypotheses'
    },
    {
      name: 'Competitive Agent',
      icon: Search,
      color: 'var(--info-500)',
      purpose: 'Market and competitor intelligence',
      capabilities: [
        'Real-time web search via Tavily AI',
        'Competitor activity tracking and scoring',
        'Industry benchmark monitoring (CPM, CPC, CTR)',
        'Market event detection and analysis',
        'Spend estimation from web mentions'
      ],
      output: 'Competitor activity reports, market insights, positioning analysis'
    },
    {
      name: 'Scenario Agent',
      icon: Zap,
      color: 'var(--accent-500)',
      purpose: 'What-if analysis and portfolio optimization',
      capabilities: [
        'Monte Carlo simulations (5,000 iterations)',
        'Sensitivity analysis (±20% budget changes)',
        'Portfolio optimization (efficient frontier)',
        'Risk/return tradeoff calculation',
        'Multi-scenario comparison'
      ],
      output: 'Expected returns with confidence intervals, risk assessments'
    },
    {
      name: 'Monitor Agent',
      icon: Activity,
      color: 'var(--error-500)',
      purpose: 'Real-time campaign monitoring and alerting',
      capabilities: [
        'Z-score anomaly detection (threshold: 2.5σ)',
        'EWMA trending analysis',
        'Spend pacing checks vs budget',
        'Creative fatigue detection',
        'Severity-based alerting (High/Medium/Low)'
      ],
      output: 'Real-time alerts with recommended actions'
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{ 
          width: '120px', 
          height: '120px', 
          margin: '0 auto 24px',
          borderRadius: '30px',
          background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '3rem',
          fontWeight: '700'
        }}>
          C
        </div>
        <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '16px' }}>Clariti</h1>
        <p style={{ fontSize: '1.5rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
          AI-Enabled Marketing Effectiveness & Spend Intelligence
        </p>
        <p style={{ fontSize: '1rem', color: 'var(--text-tertiary)' }}>Version 1.0.0 • Production-Ready Enterprise Platform</p>
      </div>

      {/* Section Navigation */}
      <div style={{ borderBottom: '2px solid var(--border-light)', marginBottom: '32px' }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          {sections.map(section => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  padding: '14px 24px',
                  border: 'none',
                  background: activeSection === section.id ? 'hsl(180, 65%, 97%)' : 'transparent',
                  borderBottom: activeSection === section.id ? '3px solid var(--accent-500)' : '3px solid transparent',
                  color: activeSection === section.id ? 'var(--accent-600)' : 'var(--text-secondary)',
                  fontWeight: activeSection === section.id ? '700' : '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.9375rem'
                }}
              >
                <Icon size={18} />
                {section.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div>
          <div className="card" style={{ marginBottom: '32px', background: 'linear-gradient(135deg, hsl(210, 100%, 98%), hsl(180, 100%, 98%))', border: '2px solid var(--accent-500)' }}>
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '16px' }}>What is Clariti?</h2>
              <p style={{ fontSize: '1.125rem', lineHeight: '1.8', maxWidth: '900px', margin: '0 auto', color: 'var(--text-primary)' }}>
                Clariti is an enterprise-grade AI platform that transforms marketing effectiveness measurement from guesswork into 
                <strong> explainable, confidence-scored recommendations</strong>. Unlike traditional analytics tools or black-box AI, 
                Clariti uses <strong>6 specialized AI agents</strong> orchestrated via LangGraph to analyze campaigns, optimize budgets, 
                and provide actionable insights with <strong>full transparency</strong>.
              </p>
            </div>
          </div>

          {/* Key Differentiators */}
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '24px' }}>Key Differentiators</h3>
          <div className="grid grid-cols-3" style={{ gap: '20px', marginBottom: '32px' }}>
            {[
              { icon: GitBranch, title: 'Multi-Agent AI', desc: '6 specialized agents vs single model', color: 'var(--primary-500)' },
              { icon: Eye, title: 'Explainable', desc: 'Confidence scores + reasoning steps', color: 'var(--accent-500)' },
              { icon: BarChart3, title: 'Statistical Rigor', desc: 'Monte Carlo 5,000+ iterations', color: 'var(--success-500)' },
              { icon: Zap, title: 'Real-Time', desc: 'Live competitive intelligence', color: 'var(--warning-500)' },
              { icon: Shield, title: 'Enterprise-Grade', desc: 'Multi-tenant, RBAC, audit trails', color: 'var(--error-500)' },
              { icon: CheckCircle, title: 'Human-in-Loop', desc: 'AI advises, humans decide', color: 'var(--info-500)' }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} style={{ 
                  padding: '24px', 
                  background: 'white',
                  border: '2px solid var(--border-light)',
                  borderRadius: '16px',
                  textAlign: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = item.color;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-light)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <Icon size={40} style={{ color: item.color, margin: '0 auto 16px' }} />
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '8px' }}>{item.title}</h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4" style={{ gap: '16px' }}>
            {[
              { label: 'Lines of Code', value: '16,112', icon: BarChart3 },
              { label: 'API Endpoints', value: '68', icon: Network },
              { label: 'AI Agents', value: '8', icon: Brain },
              { label: 'Test Coverage', value: '95%', icon: CheckCircle }
            ].map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="card" style={{ textAlign: 'center' }}>
                  <Icon size={32} style={{ margin: '0 auto 12px', color: 'var(--primary-500)' }} />
                  <p style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '4px' }}>{stat.value}</p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Capabilities Section */}
      {activeSection === 'capabilities' && (
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '24px' }}>Platform Capabilities</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {capabilities.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <div key={idx} className="card" style={{ border: `2px solid ${cap.color}` }}>
                  <div style={{ display: 'flex', gap: '24px' }}>
                    <div style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '16px', 
                      background: cap.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={40} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '8px' }}>{cap.title}</h3>
                      <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.6' }}>
                        {cap.description}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                        {cap.features.map((feature, fidx) => (
                          <div key={fidx} style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                            <CheckCircle size={16} style={{ color: cap.color, marginTop: '2px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Agents Section */}
      {activeSection === 'agents' && (
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '16px' }}>The 6 Specialized AI Agents</h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '32px', maxWidth: '800px' }}>
            Each agent is purpose-built for a specific marketing task, with its own reasoning logic, confidence calculation, 
            and explainability features. Agents collaborate via LangGraph orchestration to provide comprehensive recommendations.
          </p>

          <div className="grid grid-cols-2" style={{ gap: '24px' }}>
            {agents.map((agent, idx) => {
              const Icon = agent.icon;
              return (
                <div key={idx} className="card" style={{ border: `2px solid ${agent.color}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '14px', 
                      background: agent.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={30} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '4px' }}>{agent.name}</h3>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{agent.purpose}</p>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '10px', color: 'var(--text-secondary)' }}>Capabilities:</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {agent.capabilities.map((cap, cidx) => (
                        <div key={cidx} style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                          <ArrowRight size={14} style={{ color: agent.color, marginTop: '3px', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>{cap}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.75rem', fontWeight: '700', marginBottom: '4px', color: 'var(--text-secondary)' }}>Output:</p>
                    <p style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>{agent.output}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Orchestration */}
          <div style={{ marginTop: '32px', padding: '32px', background: 'hsl(180, 65%, 97%)', border: '2px solid var(--accent-500)', borderRadius: '16px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <GitBranch size={40} style={{ margin: '0 auto 12px', color: 'var(--accent-600)' }} />
              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px' }}>Multi-Agent Orchestration</h3>
              <p style={{ fontSize: '1rem', color: 'var(--accent-700)' }}>Powered by LangGraph</p>
            </div>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <pre style={{ 
                fontSize: '0.875rem', 
                lineHeight: '1.8',
                padding: '20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid var(--accent-500)',
                fontFamily: 'monospace',
                overflowX: 'auto'
              }}>
{`Data Agent
    ↓
Effectiveness Agent
    ↓
Scenario Agent
    ↓
Creative Agent (if assets)
    ↓
Competitive Agent (if competitors)
    ↓
Conflict Resolver (weighted voting)
    ↓
Human Review (if confidence < 70%)`}
              </pre>
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <span className="badge" style={{ background: 'var(--accent-500)', color: 'white' }}>Shared State</span>
                <span className="badge" style={{ background: 'var(--accent-500)', color: 'white' }}>Conflict Resolution</span>
                <span className="badge" style={{ background: 'var(--accent-500)', color: 'white' }}>Human Escalation</span>
                <span className="badge" style={{ background: 'var(--accent-500)', color: 'white' }}>Audit Logging</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Technical Section */}
      {activeSection === 'technical' && (
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '24px' }}>Technical Architecture</h2>
          
          <div className="grid grid-cols-2" style={{ gap: '24px', marginBottom: '32px' }}>
            <div className="card">
              <h3 className="card-title">Backend Stack</h3>
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { tech: 'FastAPI', desc: 'Python 3.11 async web framework' },
                  { tech: 'LangChain + LangGraph', desc: 'Multi-agent orchestration' },
                  { tech: 'MongoDB', desc: 'Multi-tenant database (12 collections)' },
                  { tech: 'GPT-5.2', desc: 'OpenAI via Emergent Universal Key' },
                  { tech: 'ChromaDB', desc: 'Vector store for RAG' },
                  { tech: 'Tavily + Serper', desc: 'Real-time web search' }
                ].map((item, idx) => (
                  <div key={idx} style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.9375rem', fontWeight: '700', marginBottom: '4px' }}>{item.tech}</p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="card-title">Frontend Stack</h3>
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { tech: 'React 19', desc: 'Latest React with modern hooks' },
                  { tech: 'React Router DOM 7', desc: 'Client-side routing + auth' },
                  { tech: 'Recharts 3', desc: '45+ interactive visualizations' },
                  { tech: 'Lucide React', desc: 'Professional icon system' },
                  { tech: 'Custom Design System', desc: '168-color palette' },
                  { tech: 'Axios', desc: 'HTTP client with interceptors' }
                ].map((item, idx) => (
                  <div key={idx} style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.9375rem', fontWeight: '700', marginBottom: '4px' }}>{item.tech}</p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Platform Stats */}
          <div className="grid grid-cols-3" style={{ gap: '16px' }}>
            {[
              { label: 'Backend Files', value: '53', detail: '6,967 lines of Python' },
              { label: 'Frontend Files', value: '25', detail: '9,145 lines of JS/CSS' },
              { label: 'API Endpoints', value: '68', detail: 'RESTful APIs' },
              { label: 'Pages', value: '12', detail: '9 app + 3 auth' },
              { label: 'Components', value: '100+', detail: 'Reusable React components' },
              { label: 'Charts', value: '45+', detail: 'Recharts visualizations' },
              { label: 'AI Agents', value: '8', detail: '6 core + 2 integration' },
              { label: 'Integrations', value: '4', detail: 'Platform connectors' },
              { label: 'Documentation', value: '2,200+', detail: 'Lines across 7 guides' }
            ].map((stat, idx) => (
              <div key={idx} className="card" style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-600)', marginBottom: '4px' }}>{stat.value}</p>
                <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '4px' }}>{stat.label}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{stat.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Value Proposition */}
      {activeSection === 'value' && (
        <div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '24px' }}>Value Proposition</h2>
          
          <div className="card" style={{ marginBottom: '32px', background: 'hsl(142, 76%, 96%)', border: '2px solid var(--success-500)' }}>
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <DollarSign size={48} style={{ margin: '0 auto 16px', color: 'var(--success-600)' }} />
              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '12px', color: 'var(--success-700)' }}>ROI Example</h3>
              <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div style={{ fontSize: '1.125rem', lineHeight: '2', color: 'var(--text-primary)' }}>
                  <p><strong>$10M</strong> marketing budget</p>
                  <p>× <strong>5%</strong> improvement from better allocation</p>
                  <p>= <strong>$500K</strong> additional return</p>
                  <p style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px solid var(--success-500)' }}>
                    Clariti cost: <strong>$180K/year</strong>
                  </p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-600)', marginTop: '8px' }}>
                    Net Value: $320K • ROI: 178%
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2" style={{ gap: '24px' }}>
            <div className="card">
              <h3 className="card-title">vs. Traditional Analytics</h3>
              <div style={{ marginTop: '16px' }}>
                <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-light)' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '8px', color: 'var(--error-600)' }}>Google Analytics / Adobe</p>
                  <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>Show what happened (reporting)</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '8px', color: 'var(--success-600)' }}>Clariti</p>
                  <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>Tell you what to DO next (AI recommendations)</p>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="card-title">vs. Marketing Mix Modeling</h3>
              <div style={{ marginTop: '16px' }}>
                <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-light)' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '8px', color: 'var(--error-600)' }}>MMM Vendors</p>
                  <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>Quarterly lag, black-box, $300K+/year</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '8px', color: 'var(--success-600)' }}>Clariti</p>
                  <p style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>Real-time, explainable, $180K/year</p>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '32px', padding: '24px', background: 'hsl(199, 89%, 96%)', border: '2px solid var(--info-500)', borderRadius: '12px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '16px', color: 'var(--info-700)' }}>Why Choose Clariti?</h3>
            <div className="grid grid-cols-2" style={{ gap: '16px' }}>
              {[
                'Explainable AI with confidence scores (not black-box)',
                'Real-time insights (not quarterly reports)',
                'Multi-agent intelligence (not single model)',
                'Statistical rigor (Monte Carlo 5,000+ iterations)',
                'Affordable ($180K vs $300K+ for alternatives)',
                'Enterprise-grade (multi-tenant, RBAC, audit trails)',
                'Human-in-the-loop (AI advises, you decide)',
                'Continuous learning (RLHF from your decisions)'
              ].map((reason, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
                  <CheckCircle size={20} style={{ color: 'var(--info-600)', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9375rem', lineHeight: '1.6' }}>{reason}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default About;
